/* biome-ignore-all lint/a11y/noNoninteractiveTabindex: a faixa do carrossel é uma região com `overflow-x: auto`, e região rolável PRECISA de tabIndex para ser percorrida por teclado (WCAG 2.1.1 — o axe acusa como `scrollable-region-focusable`). Aqui a regra está invertida: remover o atributo é o que criaria a barreira. */
/* biome-ignore-all lint/a11y/useSemanticElements: os `role="group"` deste arquivo são o padrão WAI-ARIA de carrossel — a faixa e cada slide. `<fieldset>` não cabe em nenhum dos dois, e `<section>` inventaria landmarks aninhados dentro da que já existe. */

"use client";
import { Pause, Play } from "lucide-react";
import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CarouselControls } from "@/components/ui/carousel-controls";
import { cn } from "@/lib/utils";

export interface CarouselItem {
  id: string;
  /** Nome do item pras bolinhas e pro leitor de tela. */
  label: string;
  /**
   * Chave de correspondência entre linhas (ex.: slug da categoria). Quando
   * presente, as outras linhas seguem o item com a mesma chave em vez de
   * andar o mesmo número de passos.
   */
  group?: string;
  content: ReactNode;
}

export interface CarouselRow {
  items: CarouselItem[];
  /** id do heading que dá nome à linha. */
  labelledBy: string;
  /** Largura (e o que mais for de tamanho) de cada slide, ex. `w-[86vw] md:w-[40rem]`. */
  slideClassName: string;
  className?: string;
}

const AUTOPLAY_MS = 6000;
const SETTLE_MS = 150;
/** Cópias renderizadas por linha: [clones] [reais] [clones]. */
const COPIES = 3;

/** Menor deslocamento (com sinal) que leva de `from` a `to` num anel de `n`. */
function shortest(from: number, to: number, n: number): number {
  const d = (((to - from) % n) + n) % n;
  return d > n / 2 ? d - n : d;
}

interface TrackHandle {
  /** Rola `delta` slides (negativo volta), pela cópia mais próxima. */
  step: (delta: number) => void;
}

interface TrackProps {
  row: CarouselRow;
  handle: Ref<TrackHandle>;
  /** Item real em que a faixa começa. */
  initial: number;
  /** Item real mais perto do centro mudou (dispara durante o scroll). */
  onActive: (i: number) => void;
  /** Scroll assentou sobre o item real `i`. */
  onSettle: (i: number) => void;
  live: "off" | "polite";
}

/**
 * Uma faixa horizontal com scroll nativo, snap centralizado e loop infinito.
 *
 * O loop é feito com clones: o conjunto real fica no meio de três cópias.
 * Quando o scroll assenta sobre um clone, a faixa salta (sem animação) pra
 * posição equivalente do conjunto real — como as três cópias são idênticas,
 * o salto é invisível.
 */
function Track({ row, handle, initial, onActive, onSettle, live }: TrackProps) {
  const { items, labelledBy, slideClassName, className } = row;
  const n = items.length;
  const trackRef = useRef<HTMLDivElement>(null);
  /** Índice absoluto (0..3n-1) do slide mais perto do centro. */
  const absRef = useRef(n);
  const callbacks = useRef({ onActive, onSettle });
  callbacks.current = { onActive, onSettle };

  const slides = useCallback(
    () => trackRef.current?.querySelectorAll<HTMLElement>("[data-slide]") ?? [],
    [],
  );

  /** scrollLeft que centraliza o slide absoluto `j`. */
  const leftFor = useCallback(
    (j: number) => {
      const track = trackRef.current;
      const card = slides()[j];
      if (!track || !card) return null;
      return card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
    },
    [slides],
  );

  const scrollToAbs = useCallback(
    (j: number, behavior: ScrollBehavior) => {
      const track = trackRef.current;
      const left = leftFor(j);
      if (!track || left === null) return;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      track.scrollTo({ left, behavior: reduce ? "instant" : behavior });
    },
    [leftFor],
  );

  useImperativeHandle(
    handle,
    () => ({
      step: (delta) => scrollToAbs(absRef.current + delta, "smooth"),
    }),
    [scrollToAbs],
  );

  // Começa no item inicial (cópia do meio), sem animação.
  useLayoutEffect(() => {
    absRef.current = n + initial;
    scrollToAbs(n + initial, "instant");
  }, [n, initial, scrollToAbs]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Item ativo = slide cujo centro está mais perto do centro da faixa.
    const update = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Number.POSITIVE_INFINITY;
      slides().forEach((card, j) => {
        const dist = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = j;
        }
      });
      absRef.current = best;
      callbacks.current.onActive(best % n);
    };

    // Scroll assentou: se parou num clone, salta pro conjunto real.
    const settle = () => {
      const all = slides();
      const first = all[0];
      const realFirst = all[n];
      if (!first || !realFirst) return;
      const setWidth = realFirst.offsetLeft - first.offsetLeft;
      const startReal = leftFor(n);
      const startAfter = leftFor(2 * n);
      if (setWidth > 0 && startReal !== null && startAfter !== null) {
        const half = realFirst.offsetWidth / 2;
        const pos = track.scrollLeft;
        if (pos < startReal - half) {
          track.scrollTo({ left: pos + setWidth, behavior: "instant" });
          absRef.current += n;
        } else if (pos > startAfter - half) {
          track.scrollTo({ left: pos - setWidth, behavior: "instant" });
          absRef.current -= n;
        }
      }
      callbacks.current.onSettle(absRef.current % n);
    };

    // Sem `scrollend` (Safari só ganhou em 2025): considera o scroll assentado
    // quando fica SETTLE_MS sem novos eventos.
    let timer = 0;
    const onScroll = () => {
      update();
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, SETTLE_MS);
    };
    const onResize = () => scrollToAbs(absRef.current, "instant");

    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(timer);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [n, slides, leftFor, scrollToAbs]);

  return (
    <section aria-labelledby={labelledBy} className={className}>
      {/*
       * `tabIndex={0}` na faixa: ela rola na horizontal, e uma região rolável
       * precisa ser alcançável por teclado (WCAG 2.1.1 — o axe acusa isso como
       * `scrollable-region-focusable`).
       *
       * Não é formalidade. Sem isso, a única forma de percorrer a faixa com o
       * teclado é tabular por todos os links dentro dela — e são três cópias
       * dos slides, o que na home dá mais de vinte paradas. Focada, a faixa
       * rola com ← → pelo comportamento nativo do navegador, e as setas e
       * bolinhas abaixo continuam funcionando como atalho.
       */}
      <div
        ref={trackRef}
        data-track
        aria-live={live}
        tabIndex={0}
        role="group"
        aria-label="Faixa rolável — use as setas do teclado para percorrer"
        className="scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:gap-5"
      >
        {Array.from({ length: COPIES }, (_, copy) =>
          items.map((item, i) => {
            const clone = copy !== 1;
            return (
              <div
                key={`${copy}-${item.id}`}
                data-slide
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} de ${n}`}
                aria-hidden={clone || undefined}
                inert={clone || undefined}
                className={cn("shrink-0 snap-center", slideClassName)}
              >
                {item.content}
              </div>
            );
          }),
        )}
      </div>
    </section>
  );
}

export interface GalleryCarouselProps {
  /** Linhas que se movem juntas. As bolinhas e o item ativo são da primeira. */
  rows: CarouselRow[];
  /** Avança sozinho a cada 6 s, com botão de pausa. */
  autoplay?: boolean;
  /** Mostra uma bolinha por item da primeira linha. */
  dots?: boolean;
  className?: string;
}

/**
 * Galeria no padrão da Apple: uma ou mais faixas infinitas que andam juntas,
 * com um único conjunto de controles abaixo da última. Setas, bolinhas e
 * autoplay mandam o mesmo passo pra todas as linhas; quando o usuário arrasta
 * uma linha, as outras recebem o mesmo número de passos.
 */
export function GalleryCarousel({
  rows,
  autoplay = false,
  dots = false,
  className,
}: GalleryCarouselProps) {
  const handles = useRef<(TrackHandle | null)[]>([]);

  /**
   * Índice real, na linha `q`, do item com a chave `group`, procurando a partir
   * de `from` na direção `dir` (+1 pra frente, -1 pra trás, 0 = o mais perto).
   * Null se não há. Andar sempre na direção do comando evita que a linha
   * secundária vá e volte enquanto a principal só avança.
   */
  const matchIndex = useCallback(
    (q: number, group: string, from: number, dir: number): number | null => {
      const items = rows[q].items;
      const n = items.length;
      if (dir === 0) {
        let best: number | null = null;
        items.forEach((item, j) => {
          if (item.group !== group) return;
          if (best === null || Math.abs(shortest(from, j, n)) < Math.abs(shortest(from, best, n))) {
            best = j;
          }
        });
        return best;
      }
      for (let k = 0; k < n; k++) {
        const j = (((from + dir * k) % n) + n) % n;
        if (items[j].group === group) return j;
      }
      return null;
    },
    [rows],
  );

  /** Item real em que cada linha deve estar depois dos comandos já dados.
   *  As linhas secundárias começam no item que casa com o primeiro da primeira. */
  const [initial] = useState<number[]>(() => {
    const group = rows[0]?.items[0]?.group;
    return rows.map((_, q) =>
      q === 0 || group === undefined ? 0 : (matchIndex(q, group, 0, 0) ?? 0),
    );
  });
  const expected = useRef<number[]>([...initial]);
  const [active, setActive] = useState(0);
  const [userPaused, setUserPaused] = useState(!autoplay);
  /** Foco vindo do teclado pausa o autoplay; foco por clique ou toque, não.
   *  O último gesto diz de onde veio o foco. */
  const [focused, setFocused] = useState(false);
  const pointerRef = useRef(false);
  const [touching, setTouching] = useState(false);
  /** Cresce a cada troca de slide (automática, por clique ou por arrasto):
   *  reinicia a contagem dos 6 s e remonta o anel de progresso do zero. */
  const [cycle, setCycle] = useState(0);

  // Quem pede menos movimento não recebe autoplay; pode ligar pelo botão.
  useEffect(() => {
    if (autoplay && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setUserPaused(true);
    }
  }, [autoplay]);

  const stepRow = useCallback(
    (r: number, delta: number) => {
      const n = rows[r].items.length;
      expected.current[r] = (((expected.current[r] + delta) % n) + n) % n;
      handles.current[r]?.step(delta);
    },
    [rows],
  );

  /**
   * A linha `r` foi pro item `i` (andando `d` passos). As outras linhas seguem:
   * pela chave `group` do item, se houver; senão, pelos mesmos `d` passos.
   */
  const propagate = useCallback(
    (r: number, i: number, d: number) => {
      const group = rows[r].items[i]?.group;
      rows.forEach((linha, q) => {
        if (q === r) return;
        if (group === undefined) {
          stepRow(q, d);
          return;
        }
        const from = expected.current[q];
        const target = matchIndex(q, group, from, Math.sign(d));
        if (target === null || target === from) return;
        // Distância na direção do comando (nunca o atalho pelo outro lado).
        const n = linha.items.length;
        const ahead = ((((target - from) * Math.sign(d)) % n) + n) % n;
        stepRow(q, Math.sign(d) * ahead);
      });
    },
    [rows, stepRow, matchIndex],
  );

  const stepAll = useCallback(
    (delta: number) => {
      const n = rows[0].items.length;
      const target = (((expected.current[0] + delta) % n) + n) % n;
      stepRow(0, delta);
      propagate(0, target, delta);
      setCycle((c) => c + 1);
    },
    [rows, stepRow, propagate],
  );

  const goTo = (i: number) => stepAll(shortest(expected.current[0], i, rows[0].items.length));

  /** Uma linha assentou. Se foi o usuário que a moveu, as outras seguem. */
  const onSettle = useCallback(
    (r: number, i: number) => {
      const d = shortest(expected.current[r], i, rows[r].items.length);
      if (d === 0) return;
      expected.current[r] = i;
      propagate(r, i, d);
      setCycle((c) => c + 1);
    },
    [rows, propagate],
  );

  const running = autoplay && !userPaused && !focused && !touching;

  // Um timeout por ciclo, não um interval: qualquer troca de slide zera a
  // contagem (via `cycle`), então o anel e o avanço ficam sempre em sincronia.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `cycle` não é lido no efeito de propósito — é o gatilho que rearma o timeout a cada troca.
  useEffect(() => {
    if (!running) return;
    const id = window.setTimeout(() => {
      if (document.hidden) {
        setCycle((c) => c + 1);
        return;
      }
      stepAll(1);
    }, AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [running, cycle, stepAll]);

  const primeira = rows[0];

  return (
    <section
      aria-roledescription="carrossel"
      aria-label="Galeria de destaques"
      className={className}
      onPointerDown={() => {
        pointerRef.current = true;
      }}
      onKeyDown={() => {
        pointerRef.current = false;
      }}
      onFocus={() => {
        if (!pointerRef.current) setFocused(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      onTouchStart={() => setTouching(true)}
      onTouchEnd={() => setTouching(false)}
      onTouchCancel={() => setTouching(false)}
    >
      {rows.map((linha, r) => (
        <Track
          key={linha.labelledBy}
          row={linha}
          initial={initial[r]}
          handle={(h) => {
            handles.current[r] = h;
          }}
          onActive={r === 0 ? setActive : () => {}}
          onSettle={(i) => onSettle(r, i)}
          live={running ? "off" : "polite"}
        />
      ))}

      <CarouselControls
        items={dots ? primeira.items.map((item) => ({ id: item.id, label: item.label })) : []}
        active={active}
        onPrevious={() => stepAll(-1)}
        onNext={() => stepAll(1)}
        onGo={goTo}
      >
        {autoplay && (
          <span className="relative ml-2 h-10 w-10">
            <button
              type="button"
              onClick={() => setUserPaused((p) => !p)}
              aria-pressed={userPaused}
              aria-label={userPaused ? "Retomar avanço automático" : "Pausar avanço automático"}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-fg-strong/20 text-fg-strong transition-colors hover:bg-bg-secondary"
            >
              {userPaused ? (
                <Play aria-hidden className="h-4 w-4" />
              ) : (
                <Pause aria-hidden className="h-4 w-4" />
              )}
            </button>
            {/* Anel que se preenche nos 6 s do ciclo. `key={cycle}` remonta o
                SVG a cada troca, reiniciando a animação do zero. */}
            {running && (
              <svg
                key={cycle}
                data-progress
                role="presentation"
                aria-hidden="true"
                viewBox="0 0 40 40"
                className="pointer-events-none absolute inset-0 -rotate-90 text-primary"
              >
                <circle
                  cx="20"
                  cy="20"
                  r="19"
                  pathLength={1}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="1"
                  style={{ animation: `ring-fill ${AUTOPLAY_MS}ms linear forwards` }}
                />
              </svg>
            )}
          </span>
        )}
      </CarouselControls>
    </section>
  );
}
